import {
	ActionListAnchor,
	ActionListCustom,
	ActionListInfo,
	ActionListItem,
	BusyIndicator,
	ButtonBase,
	DropOverActionList,
	Icon,
	PersonAvatar,
	Portal
} from "client/Components";
import {
	ComputedDisplayNameResult,
	getComputedDisplayName
} from "client/Features/AddressBook/common";
import { UserIdentityModel, getUserProfileImageUrl } from "client/Api/UserProfile/service";
import {
	generateTrackingId,
	isNotNil
} from "client/Misc/utils";
import {
	ReactElement,
	RefObject,
	useCallback,
	useMemo,
	useRef,
	useState
} from "react";
import styles from "./UserProfileMenu.module.less";
import { ApplicationCultureSelect } from "client/Features/Users/Culture/ApplicationCultureSelect";
import { getCurrentCulture, localize } from "client_legacy/ndaw/culture";
import { setUserCulture } from "client/Api/Localization/userCulture";
import { createUrlRelativeToEnvironment, reloadCurrentPage } from "client_legacy/ndaw/navigator";
import {
	AlertManager,
	MediaQueryKind,
	setDocumentTheme,
	useAlerts,
	useConfiguredTheme,
	useUnmountedCancellation,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { ThemeSelect } from "./ThemeSelect";
import { Theme, setUserTheme } from "client/Api/Branding/UserTheme/userTheme";
import { userConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { roadmapRouting } from "client/Features/Roadmap/RoadmapPage";
import { getTermsArticles } from "client/Features/Terms/termsArticles";

export type UserProfileMenuProps = {
	currentUserIdentity: UserIdentityModel;
	onOpenMobileDialog(): void;
	onOpenFeedbackDialog(): void;
};

const currentCulture: string = getCurrentCulture();
const acceptableUsePolicyUrl: string = getTermsArticles().acceptableUsePolicy
	.url;

export const UserProfileMenu = (props: UserProfileMenuProps): ReactElement => {
	const {
		currentUserIdentity,
		onOpenMobileDialog,
		onOpenFeedbackDialog
	} = props;

	const [isActive, setIsActive] = useState<boolean>(false);
	const [isBusy, setIsBusy] = useState<boolean>(false);

	const buttonRef: RefObject<HTMLButtonElement | null> = useRef<HTMLButtonElement>(null);
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);

	const currentTheme: Theme = useConfiguredTheme();

	const userDisplayInfo = useMemo(
		(): ComputedDisplayNameResult => {
			return getComputedDisplayName(currentUserIdentity);
		},
		[currentUserIdentity]
	);

	const avatarUrl = useMemo(
		(): string | null => {
			return isNotNil(currentUserIdentity.avatarId)
				? getUserProfileImageUrl(currentUserIdentity.avatarId)
				: null;
		},
		[currentUserIdentity.avatarId]
	);

	const { isAdmin } = userConfiguration();

	const alertManager: AlertManager = useAlerts();

	const cultureChangeHandler = useCallback(
		async (newCulture: string): Promise<void> => {
			if (newCulture === currentCulture) {
				return;
			}

			try {
				setIsBusy(true);
				await setUserCulture(newCulture, globalCancellation);
				reloadCurrentPage();
			} catch(e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
				alertManager.showError(localize("_GeneralErrorMessage"));
				setIsBusy(false);
			}
		},
		[alertManager, globalCancellation]
	);

	const themeChangeHandler = useCallback(
		async (newTheme: Theme): Promise<void> => {
			if (newTheme === currentTheme) {
				return;
			}

			setDocumentTheme(newTheme);
			setIsActive(false);

			try {
				await setUserTheme(newTheme, globalCancellation);

				if (window.mobileApp
					&& window.mobileApp.changeTheme
				) {
					window.mobileApp
						.changeTheme(newTheme);
				}
			} catch(e) {
				setDocumentTheme(currentTheme);
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
				alertManager.showError(localize("_GeneralErrorMessage"));
			}
		},
		[
			alertManager,
			currentTheme,
			globalCancellation
		]
	);

	const mobileAppClickHandler = useCallback(
		(): void => {
			if (window.mobileApp) {
				window.mobileApp
					.openNativeMobileSettingsPage(null!);
			} else {
				onOpenMobileDialog();
			}
		},
		[onOpenMobileDialog]
	);

	return (
		<>
			<ButtonBase
				ref={buttonRef}
				className={styles.button}
				onClick={() => setIsActive(true)}
				trackingId="profile-menu-button"
			>
				<PersonAvatar
					initials={userDisplayInfo.initials}
					imageSrc={avatarUrl}
					size="small"
					trackingId="user-avatar"
				/>
				<Icon
					value="chevron-down"
				/>
			</ButtonBase>
			{isActive && isNotNil(buttonRef.current) && (
				<DropOverActionList
					target={buttonRef.current}
					targetAlignment="br"
					contentAlignment="tr"
					onClose={() => setIsActive(false)}
					menuTitle={userDisplayInfo.computedDisplayName}
					trackingId={generateTrackingId("profile_menu", "__drop-over")}
					width="max"
					shouldCloseWhenWindowLoosesFocus
				>
					{!isResponsive && (
						<ActionListInfo
							trackingId="display-name"
							text={userDisplayInfo.computedDisplayName}
						/>
					)}

					<ActionListCustom>
						<ApplicationCultureSelect
							value={currentCulture}
							onChange={cultureChangeHandler}
							trackingId="culture-select"
							menuTitle={localize("_Language")}
						/>
					</ActionListCustom>

					<ActionListCustom>
						<ThemeSelect
							value={currentTheme}
							onChange={themeChangeHandler}
							trackingId="theme-select"
							menuTitle={localize("_Theme")}
						/>
					</ActionListCustom>

					<ActionListAnchor
						text={localize("_Settings")}
						icon="gear"
						url={createUrlRelativeToEnvironment("UserProfile")}
						trackingId="settings-item"
					/>
					<ActionListItem
						text={localize("_MobileApp")}
						icon="mobile-notch"
						onClick={mobileAppClickHandler}
						trackingId="mobile-app-item"
					/>
					<ActionListAnchor
						text={localize("_Support")}
						icon="circle-question"
						url={createUrlRelativeToEnvironment("Support")}
						trackingId="support-item"
					/>

					{isAdmin && (
						<ActionListAnchor
							text={localize("_Roadmap")}
							icon="map"
							url={createUrlRelativeToEnvironment(roadmapRouting.page)}
							trackingId="roadmap-item"
						/>
					)}

					<ActionListItem
						text={localize("_Feedback")}
						icon="comment"
						onClick={onOpenFeedbackDialog}
						trackingId="feedback-item"
					/>
					<ActionListAnchor
						text={localize("_TermsAndPrivacy")}
						icon="shield-check"
						url={acceptableUsePolicyUrl}
						shouldOpenInNewWindow
						trackingId="terms-and-privacy-item"
					/>
					<ActionListAnchor
						text={localize("_SignOut")}
						icon="arrow-right-to-bracket"
						url={createUrlRelativeToEnvironment("SignOut")}
						trackingId="sign-out-item"
					/>
				</DropOverActionList>
			)}
			{isBusy && (
				<Portal container={document.body}>
					<BusyIndicator />
				</Portal>
			)}
		</>
	);
};
UserProfileMenu.displayName = "UserProfileMenu";
